import { CUSTOMER } from "../../api/routes";
import type { ApiClient } from "../../api/types";
import { refusalOf } from "../partners/port";
import type { Refusal } from "../partners/port";
import type { Sale } from "../types";

/**
 * The customer's OTP check (DISCOUNT_V2_PLAN.md §9.10, decision 80): some discounts count only for a
 * verified customer, and the cashier verifies them with a code texted to the customer's phone.
 *
 * The backend texts the code and answers only its hash, an HMAC-MD5 keyed on the customer's ref id
 * (`customer_controller.js` `initiateAuthentication`, 2007-2008), so the typed code is checked here,
 * on the terminal, and nothing more is sent. Rewrite of v1's `helpers/saleHelpers` › `fetchCustomerOtpHash`
 * and `verifyOtpHash`, which reached the same route through a v1 saga and kept the hash in
 * `customerStore`; here the hash stays with whoever asked for it.
 */

/**
 * `jshashes` ships no type declarations, so it is required, as v1's `helpers/saleHelpers` does.
 * Only `MD5.hex_hmac(key, data)` is used.
 */
// eslint-disable-next-line @typescript-eslint/no-require-imports
const Hashes = require("jshashes");

/** Does the typed code match the hash the backend answered for this customer? */
export function otpMatches(hash: string, customerRefId: string, otp: string): boolean {
  if (!hash || !customerRefId) return false;
  return new Hashes.MD5().hex_hmac(customerRefId, otp.trim()) === hash;
}

/** The sale with its customer marked verified: what every verified-customer discount reads (`customerInfo.authenticated`). */
export const withCustomerVerified = (sale: Sale): Sale => ({
  ...sale,
  customerInfo: { ...(sale.customerInfo ?? {}), authenticated: true },
});

/** The customer the code goes to; `undefined` when the bill has none. */
export const customerRefIdOf = (sale: Sale | null | undefined): string | undefined => {
  const id = sale?.customerInfo?.customerRefId;
  return typeof id === "string" && id !== "" ? id : undefined;
};

export type CustomerOtpOutcome = { kind: "sent"; hash: string } | ({ kind: "failed" } & Refusal);

export interface CustomerOtp {
  readonly routes: typeof CUSTOMER_OTP_ROUTES;
  /** Text the bill's customer a code, for the order's branch (decision 7); answers the code's hash. */
  send(sale: Sale): Promise<CustomerOtpOutcome>;
}

export const CUSTOMER_OTP_ROUTES = [CUSTOMER.authenticate] as const;

/** A sale with no customer, or one the backend has no phone number for, cannot be texted. */
export const NO_CUSTOMER = "NO_CUSTOMER";

export function createCustomerOtp({ call }: { call: ApiClient["call"] }): CustomerOtp {
  return {
    routes: CUSTOMER_OTP_ROUTES,
    async send(sale) {
      const customerRefId = customerRefIdOf(sale);
      if (customerRefId === undefined) return { kind: "failed", code: NO_CUSTOMER };
      const result = await call(CUSTOMER.authenticate, { body: { branchId: sale.branchId, customerRefId } });
      if (!result.ok) return { kind: "failed", ...refusalOf(result.error) };
      const hash = (result.data as { hash?: unknown } | undefined)?.hash;
      return typeof hash === "string" && hash !== "" ? { kind: "sent", hash } : { kind: "failed" };
    },
  };
}

/** The phone as v1's OTP prompt shows it: the first digit and the last two, the rest starred (V1_DUPLICATES.md D91). */
export function maskPhone(phone: string): string {
  if (phone.length <= 3) return phone;
  return `${phone.slice(0, 1)}${"*".repeat(phone.length - 3)}${phone.slice(-2)}`;
}
